// CWR 3.1 Lookup Tables for TypeScript
// Based on CWR 3.1.0 documentation.
// Note: Some tables like TIS Codes and Language Codes are extensive and
// are often managed via external files or databases in production applications.

/**
 * Writer Role Codes
 * Defines the creative contribution of a writer.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const WriterRoleCodes = {
  A: "Author / Author of Lyrics",
  AD: "Adaptor",
  AR: "Arranger",
  C: "Composer",
  CA: "Composer/Author", // Typically Composer and Author of Lyrics
  PA: "Income Participant", // Non-creative participant
  SA: "Sub-Author", // e.g., sub-lyricist for a specific territory
  SR: "Sub-Arranger",
  TR: "Translator",
} as const;
export type WriterRoleCode = keyof typeof WriterRoleCodes;

/**
 * Publisher Role Codes
 * Defines the role of a publisher in relation to a work or writer.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const PublisherRoleCodes = {
  AM: "Administrator", // Administrator
  AQ: "Acquirer", // Acquirer (Original Publisher who has acquired rights from another OP)
  E: "Original Publisher", // Original Publisher
  ES: "Substituted Publisher", // Publisher acting in place of Original Publisher
  PA: "Income Participant", // Non-publishing income participant
  SE: "Sub-Publisher", // Sub-Publisher
} as const;
export type PublisherRoleCode = keyof typeof PublisherRoleCodes;

/**
 * Musical Work Distribution Category Codes
 * Classifies the work for distribution purposes.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const MusicalWorkDistributionCategoryCodes = {
  JAZ: "Jazz",
  POP: "Popular",
  SER: "Serious", // Classical, Symphonic, etc.
  UNC: "Unclassified",
} as const;
export type MusicalWorkDistributionCategoryCode =
  keyof typeof MusicalWorkDistributionCategoryCodes;

/**
 * Text-Music Relationship Codes
 * Indicates the nature of the work's components.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const TextMusicRelationshipCodes = {
  MTX: "Music and Text",
  MUS: "Music Only",
  TXT: "Text Only",
} as const;
export type TextMusicRelationshipCode =
  keyof typeof TextMusicRelationshipCodes;

/**
 * Title Type Codes (for ALT record)
 * Specifies the type of an alternative title.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const TitleTypeCodes = {
  AT: "Alternative Title", // General alternative title
  AL: "Abbreviated Title",
  FT: "Formal Title", // Often used for classical works
  IT: "Incorrect Title", // A title by which the work is known but which is incorrect
  OL: "Original Language Title", // Title in its original language
  TE: "First Line of Text", // First line of lyrics used as a title
  TT: "Translated Title", // Title translated into another language
} as const;
export type TitleTypeCode = keyof typeof TitleTypeCodes;

/**
 * Territory Information System (TIS) Codes
 * Standard numeric codes for countries and regions.
 * Source: CISAC TIS Standard (Full list is extensive)
 * These are examples. A complete list should be sourced from official CISAC documentation.
 */
export const TisCodes = {
  "2136": "World",
  "0840": "United States of America",
  "0826": "United Kingdom",
  "0032": "Argentina",
  "0250": "France",
  "0276": "Germany",
  "0724": "Spain",
  "2120": "Europe (CISAC TIS definition)",
  // ... many more codes
} as const;
// It's often better to have a function to lookup TIS codes by name or a more comprehensive data structure.
// export type TisCode = keyof typeof TisCodes; // This would only cover the examples

/**
 * ISO 639-1 Language Codes
 * Standard two-letter codes for languages.
 * Source: ISO 639-1 Standard (Full list is extensive)
 * These are examples.
 */
export const LanguageAlpha2Codes = {
  EN: "English",
  ES: "Spanish",
  FR: "French",
  DE: "German",
  PT: "Portuguese",
  IT: "Italian",
  // ... many more codes
} as const;
// export type LanguageAlpha2Code = keyof typeof LanguageAlpha2Codes; // This would only cover the examples


/**
 * ISRC Validity Indicator Codes (for REC record)
 * Indicates the status of a provided ISRC.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const IsrcValidityIndicatorCodes = {
  Y: "Valid ISRC",
  N: "ISRC Invalid (syntactically incorrect or does not exist)",
  U: "Link Invalid (ISRC is valid but does not identify this work/version)",
} as const;
export type IsrcValidityIndicatorCode =
  keyof typeof IsrcValidityIndicatorCodes;


/**
 * Identifier Type Codes (for XRF record - Work ID Cross Reference)
 * Specifies the type of identifier being cross-referenced.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const IdentifierTypeCodes = {
  W: "Work Identifier", // Society or other Work ID
  R: "Recording Identifier", // Other recording ID (not ISRC)
  P: "Product Identifier", // e.g., Album UPC
  V: "Video Identifier", // e.g., ISAN for audiovisual work
  // Add other codes as per CWR specification if needed
} as const;
export type IdentifierTypeCode = keyof typeof IdentifierTypeCodes;

/**
 * Agreement Type Codes (for PWR - Publisher for Writer record)
 * Defines the type of agreement between a writer and publisher.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const AgreementTypeCodes = {
  OG: "Original General", // Writer to Original Publisher (General - all works)
  OS: "Original Specific", // Writer to Original Publisher (Specific - this work only)
  PG: "Subpublishing General", // Original Publisher to Sub-Publisher (General)
  PS: "Subpublishing Specific", // Original Publisher to Sub-Publisher (Specific)
  SA: "Sales Agency", // Sales Agency Agreement
  AM: "Administration", // Administration Agreement
} as const;
export type AgreementTypeCode = keyof typeof AgreementTypeCodes;

/**
 * Character Set Codes (for HDR record)
 * Defines the character set used in the CWR file.
 * Source: CWR 3.1 Lookup Table r0 (CWR22-0145) / CWR 3.1 User Manual
 */
export const CharacterSetCodes = {
  ISO_8859_1: "ISO-8859-1", // Latin 1 (Default for Western European languages)
  ISO_8859_2: "ISO-8859-2", // Latin 2 (Central/Eastern European)
  ISO_8859_5: "ISO-8859-5", // Cyrillic
  ISO_8859_7: "ISO-8859-7", // Greek
  JIS_X_0208: "JIS X 0208", // Japanese
  UTF_8: "UTF-8",         // Unicode (Widely compatible)
  // KSC_5601: "KSC 5601", // Korean - Note: CWR 3.1 manual might list specific names like KS_C_5601-1987
  // GB_2312: "GB 2312",   // Simplified Chinese - Note: CWR 3.1 manual might list specific names like GB_2312-1980
} as const;
export type CharacterSetCode = keyof typeof CharacterSetCodes;


// Example usage:
// const role: WriterRoleCode = 'CA';
// console.log(WriterRoleCodes[role]); // Output: Composer/Author

// const territoryName = TisCodes["0032"];
// console.log(territoryName); // Output: Argentina
